import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { Settings } from 'luxon'

import enAi from 'locales/en/ai.json'
import enAsset from 'locales/en/asset.json'
import enCommon from 'locales/en/common.json'
import enContact from 'locales/en/contact.json'
import enEnums from 'locales/en/enums.json'
import enErrors from 'locales/en/errors.json'
import enHome from 'locales/en/home.json'
import enLayout from 'locales/en/layout.json'
import enMenu from 'locales/en/menu.json'
import enModals from 'locales/en/modals.json'
import enOrders from 'locales/en/orders.json'
import enPayment from 'locales/en/payment.json'
import enProfile from 'locales/en/profile.json'
import enProject from 'locales/en/project.json'
import enUpload from 'locales/en/upload.json'
import huAi from 'locales/hu/ai.json'
import huAsset from 'locales/hu/asset.json'
import huCommon from 'locales/hu/common.json'
import huContact from 'locales/hu/contact.json'
import huEnums from 'locales/hu/enums.json'
import huErrors from 'locales/hu/errors.json'
import huHome from 'locales/hu/home.json'
import huLayout from 'locales/hu/layout.json'
import huMenu from 'locales/hu/menu.json'
import huModals from 'locales/hu/modals.json'
import huOrders from 'locales/hu/orders.json'
import huPayment from 'locales/hu/payment.json'
import huProfile from 'locales/hu/profile.json'
import huProject from 'locales/hu/project.json'
import huUpload from 'locales/hu/upload.json'

import {
  FALLBACK_LANGUAGE,
  PSEUDO_LANGUAGE,
  getBrowserLanguage,
  getStoredLanguage,
  getSupportedLanguages,
  normalizeLanguage,
  resolveLanguage,
  setStoredLanguage,
} from './language'
import { pseudoizeResources } from './pseudo'

const en = {
  ai: enAi,
  asset: enAsset,
  common: enCommon,
  contact: enContact,
  enums: enEnums,
  errors: enErrors,
  home: enHome,
  layout: enLayout,
  menu: enMenu,
  modals: enModals,
  orders: enOrders,
  payment: enPayment,
  profile: enProfile,
  project: enProject,
  upload: enUpload,
}

export const NAMESPACES = [
  'ai',
  'asset',
  'common',
  'contact',
  'enums',
  'errors',
  'home',
  'layout',
  'menu',
  'modals',
  'orders',
  'payment',
  'profile',
  'project',
  'upload',
]

export const SUPPORTED_LANGUAGES = getSupportedLanguages()

const resources = {
  en,
  hu: {
    ai: huAi,
    asset: huAsset,
    common: huCommon,
    contact: huContact,
    enums: huEnums,
    errors: huErrors,
    home: huHome,
    layout: huLayout,
    menu: huMenu,
    modals: huModals,
    orders: huOrders,
    payment: huPayment,
    profile: huProfile,
    project: huProject,
    upload: huUpload,
  },
}

if (SUPPORTED_LANGUAGES.includes(PSEUDO_LANGUAGE)) {
  resources[PSEUDO_LANGUAGE] = pseudoizeResources(en)
}

export const KEY_REVEAL_STORAGE_KEY = 'ce-i18n-key-reveal'

const isKeyRevealEnabled = () => {
  if (process.env.NODE_ENV === 'production') return false

  try {
    if (new URLSearchParams(window.location.search).get('i18nKeys') === '1') return true
    return window.localStorage.getItem(KEY_REVEAL_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export const setKeyReveal = (enabled) => {
  try {
    window.localStorage.setItem(KEY_REVEAL_STORAGE_KEY, enabled ? '1' : '0')
  } catch {
    // Storage can be unavailable; the query flag still works.
  }

  return i18n.changeLanguage(i18n.resolvedLanguage)
}

if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  window.setKeyReveal = setKeyReveal
}

const applyLocale = (language) => {
  const current = normalizeLanguage(language) || FALLBACK_LANGUAGE
  Settings.defaultLocale = current === PSEUDO_LANGUAGE ? FALLBACK_LANGUAGE : current

  if (typeof document !== 'undefined') {
    document.documentElement.lang = Settings.defaultLocale
  }
}

const keyRevealPostProcessor = {
  type: 'postProcessor',
  name: 'keyReveal',
  process: (value, key, options) => (isKeyRevealEnabled() ? `${options.ns || 'common'}:${key}` : value),
}

i18n.use(keyRevealPostProcessor).use(initReactI18next).init({
  resources,
  lng: resolveLanguage({ browserLanguage: getBrowserLanguage(), storedLanguage: getStoredLanguage() }),
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  nonExplicitSupportedLngs: true,
  load: 'languageOnly',
  ns: NAMESPACES,
  defaultNS: 'common',
  returnEmptyString: false,
  interpolation: { escapeValue: false },
  postProcess: ['keyReveal'],
  debug: process.env.NODE_ENV === 'development',
})

applyLocale(i18n.language)

i18n.on('languageChanged', applyLocale)

// Explicit user choice: persist the override so it wins over the market default on reload.
export const changeLanguage = (language) => {
  const normalized = setStoredLanguage(language)
  return normalized ? i18n.changeLanguage(normalized) : Promise.resolve()
}

export { getSupportedLanguages, normalizeLanguage }

export default i18n
